import {
  type AnyRoute,
  getRouteApi,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { isEmpty } from "lodash-es";

import type { FileRoutesById } from "@/routeTree.gen";

import { useFilterUrlKeys } from "../filter-url-keys";
import {
  hasDerivedOperator,
  operatorsForField,
  resolveOperator,
} from "../operators";
import {
  parseFilterSearch,
  serializeFilterSearch,
} from "../parse-filter-search";
import type { FilterField, FilterOp } from "../types";
import type { UnwrapOperators } from "../with-operators";

type RouteSearch<TId extends keyof FileRoutesById> = Extract<
  FileRoutesById[TId],
  AnyRoute
>["types"]["fullSearchSchema"];

/**
 * The route's filter object as CONSUMERS see it. `withOperators` widens each
 * field's schema to also accept the operator-nested URL form, but
 * `parseFilterSearch` strips that wrapper before anything reads `filters` — so a
 * typed read stays the plain value type (`string[]`, not
 * `string[] | Record<string, string[]>`).
 */
type FiltersOf<TId extends keyof FileRoutesById> =
  RouteSearch<TId> extends {
    filters?: infer F;
  }
    ? { [K in keyof NonNullable<F>]: UnwrapOperators<NonNullable<F>[K]> }
    : Record<string, unknown>;

// TanStack search is dynamically shaped per route, hence the loose record.
type AnySearch = Record<string, any>;

/**
 * Typed read/modify/clear of URL filter state. Recovers the current route's
 * `fullSearchSchema` so `filters` is the route's typed filter object, not
 * `Record<string, any>`. Self-contained port of the old `useGetFilters` /
 * `useModifyFilters` / `useClearFilter` / `useClearAllFilters` behavior
 * (preserves the `filtersOrder` comma-string maintenance).
 *
 * The URL nests a chosen operator under its filter (`filters[status][notIn]=A`);
 * this hook is the read/write boundary that hides that (see
 * `parse-filter-search.ts`). Consumers see a flat value map, and the operator
 * only through `operatorFor(field)`.
 *
 * Pass the route id for typed reads in feature code:
 *   const { filters } = useFilters<"/admin/customers/">();
 * Generic widgets call it bare; they operate on `field.key` dynamically.
 *
 * The URL keys come from the nearest `FilterUrlKeysProvider` (the default
 * `filters` / `filtersOrder` / `search` without one).
 */
export function useFilters<
  TId extends keyof FileRoutesById = keyof FileRoutesById,
>() {
  const router = useRouter();
  const routeId = router.state.matches.at(-1)?.routeId as TId;
  const navigate = useNavigate();
  const routeApi = getRouteApi(routeId);
  const search = routeApi.useSearch() as AnySearch;
  const keys = useFilterUrlKeys();

  const parsed = parseFilterSearch(search[keys.filters]);
  const filters = parsed.filters as FiltersOf<TId>;
  /** Operators the URL states explicitly; a key absent here runs on its field's default. */
  const chosenOperators = parsed.filtersOp;
  const filtersOrder: string[] = search[keys.filtersOrder]
    ? String(search[keys.filtersOrder]).split(",")
    : [];

  const nextOrder = (nextFilters: AnySearch, prevOrder: string[]) => {
    const order = [...prevOrder];
    Object.entries(nextFilters).forEach(([key, value]) => {
      const inOrder = order.includes(key);
      if (!inOrder && value !== undefined) order.push(key);
    });
    return order;
  };

  const commit = (
    nextFilters: Record<string, unknown>,
    nextOperators: Record<string, string | undefined>,
    order: string[] = nextOrder(nextFilters, filtersOrder),
  ) => {
    const newSearch: AnySearch = {
      ...search,
      [keys.filters]: serializeFilterSearch(nextFilters, nextOperators),
      [keys.filtersOrder]: isEmpty(order) ? undefined : order.join(","),
    };
    navigate({
      search: newSearch as Parameters<typeof navigate>[0]["search"],
      replace: true,
    });
  };

  /**
   * The field's active operator — the URL's explicit choice for the bracket
   * types, or the one derived from the filled bounds for `dateRange` /
   * `priceRange`. One call so no consumer has to know which source applies.
   */
  const operatorFor = (
    field: Pick<FilterField, "key" | "type" | "operators">,
  ): FilterOp =>
    resolveOperator(
      field,
      chosenOperators[field.key],
      (filters as Record<string, unknown>)[field.key],
    );

  const modify = (patch: Partial<FiltersOf<TId>> | Record<string, unknown>) => {
    commit({ ...filters, ...patch }, chosenOperators);
  };

  /**
   * Switch a filter's operator. The value is left untouched — every operator a
   * field offers reads the same stored value shape, so switching (`is any of` →
   * `is none of`) re-runs the current selection under the new operator instead
   * of resetting the chip. Choosing the field's DEFAULT drops the operator from
   * the URL entirely, so the bare `filters[key]=value` form is what a default
   * always serializes back to.
   *
   * Never called for `dateRange` / `priceRange`: their operator is derived from
   * the value, and `defineFilterFields` forbids them a second operator (there
   * would be no bound to restore when switching back).
   */
  const setOperator = (
    field: Pick<FilterField, "key" | "type" | "operators">,
    operator: string,
  ) => {
    if (hasDerivedOperator(field.type)) return;
    const isDefault = operator === operatorsForField(field)[0];
    commit(filters, {
      ...chosenOperators,
      [field.key]: isDefault ? undefined : operator,
    });
  };

  /** Drops the value AND its operator — they share one URL key, so neither can be orphaned. */
  const clear = (key: string) => {
    commit(
      { ...filters, [key]: undefined },
      { ...chosenOperators, [key]: undefined },
      filtersOrder.filter((item) => item !== key),
    );
  };

  const clearAll = () => {
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        [keys.filters]: undefined,
        [keys.filtersOrder]: undefined,
      }),
      replace: true,
    });
  };

  /**
   * The empty-state reset ("Clear filters / search"): a no-match list can be
   * caused by the search box alone, so this also drops `search` — unlike
   * `clearAll`, which the chips bar uses to clear only the filter chips.
   */
  const clearAllAndSearch = () => {
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        [keys.filters]: undefined,
        [keys.filtersOrder]: undefined,
        [keys.search]: undefined,
      }),
      replace: true,
    });
  };

  return {
    routeId,
    filters,
    filtersOrder,
    operatorFor,
    modify,
    setOperator,
    clear,
    clearAll,
    clearAllAndSearch,
  };
}
